import { getFeathersClient } from "@/pages/chat/feathers-client"

export interface AttachmentCreateRequest {
	filename: string
	contentType: string
	size: number
}

export interface AttachmentCreateResponse {
	uploadUrl: string
	uploadFields: Record<string, string>
	attachmentId: string
}

export class ImageUploader {
	private feathersClient = getFeathersClient()

	async uploadImage(file: File): Promise<string> {
		const createRequest: AttachmentCreateRequest = {
			filename: file.name,
			contentType: file.type,
			size: file.size
		}

		const createResponse: AttachmentCreateResponse = await this.feathersClient
			.service('attachments')
			.create(createRequest)

		const formData = new FormData()

		Object.entries(createResponse.uploadFields).forEach(([key, value]) => {
			formData.append(key, value)
		})
		
		formData.append('file', file)

		const uploadResponse = await fetch(createResponse.uploadUrl, {
			method: 'POST',
			body: formData
		})

		if (!uploadResponse.ok) {
			//	the status goes to the console, never into the throw. `statusText` is an HTTP
			//	protocol field, not a sentence written for a user, and it is not even stable
			//	across environments -- the same policy rejection reads "Forbidden" from S3 and
			//	"FORBIDDEN" from LocalStack. The catch that toasts this can only filter by type,
			//	so provenance has to be owned here, at the throw (ha-616, ha-627).
			console.error('upload failed', uploadResponse.status, uploadResponse.statusText)
			//	"try again" is accurate rather than a placeholder: uploadImage() re-runs
			//	attachments.create() on every call, so a retry mints a fresh presigned POST with a
			//	new Expires window -- and an expired policy (init-upload.ts sets 300s) is the main
			//	failure that still reaches here, since attachmentRejectionReason() already refuses
			//	oversized and wrong-type files before they are ever attached.
			//
			//	A CAPITALISED, STANDALONE SENTENCE, and that is forced rather than a preference:
			//	message-input-2.tsx composes some of these as `Failed to send message: ${detail}`
			//	but shows attachmentRejectionReason()'s text on its own at file-select time, so
			//	every string reaching that toast has to survive both placements. Written lowercase
			//	for the composed case alone (as this one was, in ha-627) it would begin a sentence
			//	in lowercase the moment anything shows it unprefixed (ha-633).
			throw new Error('The photo could not be uploaded. Please try again.')
		}

		return createResponse.attachmentId
	}
}
